import { useCallback, useMemo, useRef, useState } from 'react';
import { faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import {
  faClapperboardPlay,
  faFileAudio,
  faImage,
  faLink,
  faMicrophone,
  faStop,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-hot-toast';
import {
  MarkdownEditor,
  MarkdownEditorHandle,
} from '@design-system/components/markdownEditor';
import { Button, TextButton } from '@design-system/components/button/button';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { InfluencerIdProvider } from '@client/new/contexts/InfluencerIdContext';
import { AddTemplatesFromLibraryModal } from '@client/new/pages/workout/components/addFromLibrary/addTemplatesFromLibraryModal';
import { EditedDraft, isAudioFile, useNewPost } from '../lib/useNewPost';
import {
  isAudioRecordingSupported,
  useAudioRecorder,
} from '../lib/useAudioRecorder';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';
import {
  communityMentionConfig,
  containsCommunityMention,
} from '../lib/channelMention';
import { useFileDropzone } from '../lib/useFileDropzone';
import { CreatorIdentity } from '../api/useCommunityCreatorIdentity';
import { useCommunityContext } from '../lib/communityContext';
import {
  ACCEPTED_AUDIO_TYPES,
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_UPLOAD_TYPES,
  MAX_AUDIO_FILES,
  MAX_MEDIA_FILES,
} from '../model/newPost.constants';
import { MentionConfirmModal } from './MentionConfirmModal';
import { NewPostMediaPreview } from './NewPostMediaPreview';
import { PostSplitButton } from './PostSplitButton';
import { SchedulePostModal } from './SchedulePostModal';

type Props = {
  author: CreatorIdentity | null;
  memberCount?: number;
  editedDraft?: EditedDraft;
  onCancelEdit?: () => void;
};

const formatElapsed = (totalSec: number): string => {
  const minutes = Math.floor(totalSec / 60);
  const seconds = String(totalSec % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const CreatorAvatar = ({ author }: { author: CreatorIdentity | null }) => {
  if (author?.avatarUrl) {
    return (
      <img
        src={author.avatarUrl}
        alt={author.name}
        className="size-10 shrink-0 rounded-full object-cover"
      />
    );
  }

  return (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#CC1EB8] text-base font-bold text-white">
      {(author?.name.charAt(0) ?? '').toUpperCase()}
    </span>
  );
};

export const NewPostField = ({
  author,
  memberCount,
  editedDraft,
  onCancelEdit,
}: Props) => {
  const { communityId, influencerId } = useCommunityContext();
  const {
    text,
    setText,
    files,
    videos,
    keptMedia,
    removeKeptMedia,
    mediaCount,
    addFiles,
    removeFile,
    addVideo,
    removeVideo,
    canSubmit,
    isSubmitting,
    isEditing,
    submit,
    saveDraft,
    schedule,
  } = useNewPost(communityId, influencerId, editedDraft);
  const editorRef = useRef<MarkdownEditorHandle>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const [showMentionConfirm, setShowMentionConfirm] = useState(false);
  const [isScheduleOpen, setScheduleOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const supportsRecording = useMemo(isAudioRecordingSupported, []);
  const hasAudioAttachment =
    files.filter(isAudioFile).length >= MAX_AUDIO_FILES;
  const recorder = useAudioRecorder({
    onRecorded: (file) => addFiles([file]),
  });
  const onRejectedFiles = useCallback((rejected: File[]) => {
    if (rejected.some((file) => file.type.startsWith('video/'))) {
      toast.error('To attach a video, use the Add Video button');
    }
  }, []);
  const { isDragging, dropzoneProps } = useFileDropzone({
    disabled: isSubmitting || recorder.isRecording,
    accept: ACCEPTED_UPLOAD_TYPES,
    onFiles: addFiles,
    onRejected: onRejectedFiles,
  });

  const creatorId = resolveInfluencerId(influencerId);

  const requestSubmit = () => {
    if (!isEditing && containsCommunityMention(text)) {
      setShowMentionConfirm(true);
      return;
    }
    void submit();
  };

  const confirmMentionSubmit = () => {
    setShowMentionConfirm(false);
    void submit();
  };

  const onFilesSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files ? Array.from(event.target.files) : [];
      if (selected.length > 0) addFiles(selected);
      event.target.value = '';
    },
    [addFiles],
  );

  return (
    <div
      className="relative flex flex-col gap-4 rounded-[10px] border border-transparent bg-white p-4 focus-within:border-[#5F1ECC]"
      {...dropzoneProps}
    >
      {isDragging && (
        <div
          data-testid="new-post-dropzone"
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[10px] border-2 border-dashed border-primary-800 bg-primary-800/10"
        >
          <FontAwesomeIcon
            icon={faPlus}
            className="text-4xl text-primary-800"
          />
        </div>
      )}
      <div className="flex gap-2.5">
        <CreatorAvatar author={author} />
        <div className="min-w-0 flex-1">
          <MarkdownEditor
            ref={editorRef}
            value={text}
            onChange={setText}
            placeholder="Tell us something..."
            disabled={isSubmitting}
            mention={communityMentionConfig}
          />
        </div>
      </div>

      <NewPostMediaPreview
        files={files}
        videos={videos}
        onRemoveFile={removeFile}
        onRemoveVideo={removeVideo}
        attached={keptMedia}
        onRemoveAttached={removeKeptMedia}
      />

      <div className="flex items-center justify-between">
        {recorder.isRecording ? (
          <div
            className="flex items-center gap-2"
            data-testid="recording-controls"
          >
            <Tooltip title="Stop Recording" variant="dark">
              <span className="flex">
                <button
                  type="button"
                  aria-label="Stop recording"
                  onClick={recorder.stop}
                  className="flex size-[42px] items-center justify-center rounded-full bg-primary-800 text-xs text-white"
                >
                  <FontAwesomeIcon icon={faStop} />
                </button>
              </span>
            </Tooltip>
            <span className="size-2 animate-pulse rounded-full bg-red-1" />
            <span className="text-sm tabular-nums text-gray-1">
              {formatElapsed(recorder.elapsedSec)}
            </span>
            <Tooltip title="Cancel Recording" variant="dark">
              <span className="flex">
                <button
                  type="button"
                  aria-label="Cancel recording"
                  onClick={recorder.cancel}
                  className="flex size-[42px] items-center justify-center rounded-full bg-primary-150 text-xs text-primary-800"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            </Tooltip>
          </div>
        ) : (
          <div className="flex gap-2">
            <Tooltip title="Add Image" variant="dark">
              <span className="flex">
                <button
                  type="button"
                  aria-label="Add image"
                  disabled={isSubmitting}
                  onClick={() => imageInputRef.current?.click()}
                  className="flex size-[42px] items-center justify-center rounded-full bg-primary-150 text-xs text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faImage} />
                </button>
              </span>
            </Tooltip>
            <Tooltip title="Add Audio" variant="dark">
              <span className="flex">
                <button
                  type="button"
                  aria-label="Add audio"
                  disabled={isSubmitting}
                  onClick={() => audioInputRef.current?.click()}
                  className="flex size-[42px] items-center justify-center rounded-full bg-primary-150 text-xs text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faFileAudio} />
                </button>
              </span>
            </Tooltip>
            <Tooltip title="Add Video" variant="dark">
              <span className="flex">
                <button
                  type="button"
                  aria-label="Add video"
                  disabled={isSubmitting || creatorId === null}
                  onClick={() => setIsVideoModalOpen(true)}
                  className="flex size-[42px] items-center justify-center rounded-full bg-primary-150 text-xs text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faClapperboardPlay} />
                </button>
              </span>
            </Tooltip>
            <Tooltip title="Add Link" variant="dark">
              <span className="flex">
                <button
                  type="button"
                  aria-label="Add link"
                  disabled={isSubmitting}
                  onClick={() => editorRef.current?.openAddLink()}
                  className="flex size-[42px] items-center justify-center rounded-full bg-primary-150 text-xs text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faLink} />
                </button>
              </span>
            </Tooltip>
            {supportsRecording && (
              <Tooltip title="Record Audio" variant="dark">
                <span className="flex">
                  <button
                    type="button"
                    aria-label="Record audio"
                    disabled={
                      isSubmitting ||
                      hasAudioAttachment ||
                      mediaCount >= MAX_MEDIA_FILES
                    }
                    onClick={() => void recorder.start()}
                    className="flex size-[42px] items-center justify-center rounded-full bg-primary-150 text-xs text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faMicrophone} />
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        )}
        <input
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(', ')}
          ref={imageInputRef}
          onChange={onFilesSelected}
          hidden
          multiple
        />
        <input
          type="file"
          accept={ACCEPTED_AUDIO_TYPES.join(', ')}
          ref={audioInputRef}
          onChange={onFilesSelected}
          hidden
        />
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <TextButton disabled={isSubmitting} onClick={onCancelEdit}>
                Cancel
              </TextButton>
              <Button
                color="primary"
                loading={isSubmitting}
                disabled={!canSubmit || recorder.isRecording}
                onClick={requestSubmit}
              >
                {isSubmitting ? null : 'Save'}
              </Button>
            </>
          ) : (
            <PostSplitButton
              disabled={!canSubmit || recorder.isRecording}
              isSubmitting={isSubmitting}
              onPost={requestSubmit}
              onSaveDraft={() => void saveDraft()}
              onSchedule={() => setScheduleOpen(true)}
            />
          )}
        </div>
      </div>

      {isScheduleOpen && (
        <SchedulePostModal
          open
          onConfirm={(when) => {
            setScheduleOpen(false);
            void schedule(when);
          }}
          onDismiss={() => setScheduleOpen(false)}
        />
      )}

      <MentionConfirmModal
        open={showMentionConfirm}
        memberCount={memberCount}
        onConfirm={confirmMentionSubmit}
        onCancel={() => setShowMentionConfirm(false)}
      />

      {isVideoModalOpen && creatorId !== null && (
        <InfluencerIdProvider value={creatorId}>
          <AddTemplatesFromLibraryModal
            isOpen
            onClose={() => setIsVideoModalOpen(false)}
            type={null}
            onAddVideo={({ uuid, previewUrl, thumbnailTimestampMs }) =>
              addVideo(
                uuid,
                previewUrl
                  ? {
                      previewUrl,
                      previewIsVideo: true,
                      previewTimeSec: (thumbnailTimestampMs ?? 0) / 1000,
                    }
                  : undefined,
              )
            }
            onAddSelection={(selectedVideos) => {
              selectedVideos.forEach((video) =>
                addVideo(
                  video.uuid,
                  video.thumbnailUrl
                    ? { previewUrl: video.thumbnailUrl }
                    : undefined,
                ),
              );
              setIsVideoModalOpen(false);
            }}
            action="add"
            uploadTags={['community']}
            attachOnUpload
          />
        </InfluencerIdProvider>
      )}
    </div>
  );
};
